import { useQuery } from '@tanstack/react-query';

import { Feedback, FeedbackApiData } from '@bonusly/common-feedback';
import { useHttpClient } from '@bonusly/http-client';
import { useUser } from '@bonusly/user-queries';

import { Bonus } from '../../../../../../../types/bonus';
import { QueryResponse } from '../../../../../types/growth-api';
import { QueryKey } from '../../../../../types/growth-query-key';
import { EventDTO } from '../../../lib/entities/event';
import { MeetingDTO } from '../../../lib/entities/meeting';
import { selectDate } from './utils';

export const useReferenceMessages = (meeting: MeetingDTO) => {
	const http = useHttpClient('v2');
	const hostId = meeting?.event?.hostId;
	const guestId = meeting?.event?.guestId;

	const { data: hostUser, isLoading: isLoadingHost } = useUser(hostId);
	const { data: guestUser, isLoading: isLoadingGuest } = useUser(guestId);

	let teamMemberId: string | undefined = undefined;

	if (!isLoadingHost && !isLoadingGuest) {
		if (hostUser?.managerId === guestId) {
			teamMemberId = hostId;
		} else if (guestUser?.managerId === hostId) {
			teamMemberId = guestId;
		} else {
			console.error('No team member found for the recognition message carousel');
		}
	}

	const biweekly = meeting?.event?.frequency === EventDTO.FREQUENCY_BIWEEKLY;
	const { startDate, endDate } = selectDate({
		biweekly,
		startedAt: meeting?.startTime,
	});

	const {
		data: bonuses,
		isLoading: isLoadingBonuses,
		isError: isErrorBonuses,
	} = useQuery(
		[QueryKey.Bonus, meeting?.id, teamMemberId],
		async () => {
			const response = await http.get<QueryResponse<Bonus[]>>('bonuses', {
				params: {
					receiver_id: teamMemberId,
					start_date: startDate,
					end_date: endDate,
				},
			});

			const bonuses = response?.data?.result;

			// TODO Add runtime validation to ensure bonuses is an array of Bonus objects
			if (Array.isArray(bonuses)) {
				return bonuses;
			} else {
				throw new Error('Unexpected response format');
			}
		},
		{
			enabled: !!teamMemberId && !!meeting?.id,
			staleTime: 1000 * 30,
		}
	);

	const {
		data: feedback,
		isLoading: isLoadingFeedback,
		isError: isErrorFeedback,
	} = useQuery(
		[QueryKey.Feedback, meeting?.id, teamMemberId],
		async () => {
			const response = await http.get<QueryResponse<FeedbackApiData[]>>('/constructive_feedback/feedback', {
				params: {
					user_id: teamMemberId,
					receiver_id: teamMemberId,
					start_date: startDate,
					end_date: endDate,
					state: 'complete',
				},
			});

			const feedbackData = response?.data;

			if (Array.isArray(feedbackData)) {
				return feedbackData.map((data) => Feedback.fromApiData(data)).filter((data) => hostId && data.sharedWithUserIds?.includes(hostId));
			} else {
				throw new Error('Unexpected response format');
			}
		},
		{
			enabled: !!teamMemberId && !!meeting?.id,
			staleTime: 1000 * 30,
		}
	);

	return {
		bonuses: bonuses,
		feedback: feedback,
		isLoading: isLoadingBonuses || isLoadingFeedback,
		isError: isErrorBonuses && isErrorFeedback,
		startDate,
		endDate,
	};
};
